import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Stars as StarsIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { branchesApi } from '../../services/api';
import { Branch } from '../../types/branch';
import ContactTransactions from './ContactTransactions';
import CustomerLoyaltyPoints from './CustomerLoyaltyPoints';

interface Contact {
  _id: string;
  type: 'customer' | 'supplier';
  name: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  category: 'regular' | 'premium' | 'vip';
  status: 'active' | 'inactive';
  taxNumber?: string;
  branches?: string[];
  notes?: Array<{
    content: string;
    createdAt: string;
  }>;
  linkedAccount?: boolean;
  accountId?: {
    _id: string;
    accountNumber: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      fetchContact();
      fetchBranches();
    }
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/contacts/${id}`);
      setContact(response.data);
    } catch (err: any) {
      console.error('Error fetching contact:', err);
      setError(err.response?.data?.error || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchesApi.getBranches({ page: 1, limit: 100 });
      if (response && response.branches) {
        setBranches(response.branches);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    navigate(`/contacts/edit/${id}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contact) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'لم يتم العثور على البيانات'}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          العودة
        </Button>
      </Box>
    );
  }

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      regular: 'عادي',
      premium: 'مميز',
      vip: 'VIP'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: any } = {
      regular: 'default',
      premium: 'primary',
      vip: 'secondary'
    };
    return colors[category] || 'default';
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '0 0 16px 16px',
          background: contact.type === 'customer' 
            ? 'linear-gradient(120deg, #1976d2 0%, #2196f3 100%)'
            : 'linear-gradient(120deg, #00796b 0%, #009688 100%)',
          color: 'white'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleBack} sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {contact.name}
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  icon={contact.type === 'customer' ? <PersonIcon /> : <BusinessIcon />}
                  label={contact.type === 'customer' ? 'عميل' : 'مورد'}
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
                <Chip
                  label={getCategoryLabel(contact.category)}
                  color={getCategoryColor(contact.category)}
                  sx={{ fontWeight: 'bold' }}
                />
                <Chip
                  label={contact.status === 'active' ? 'نشط' : 'غير نشط'}
                  color={contact.status === 'active' ? 'success' : 'error'}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            تعديل
          </Button>
        </Box>
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* معلومات الاتصال */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  معلومات الاتصال
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {contact.phone && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <PhoneIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        رقم الهاتف
                      </Typography>
                      <Typography variant="body1">{contact.phone}</Typography>
                    </Box>
                  </Box>
                )}

                {contact.email && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <EmailIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        البريد الإلكتروني
                      </Typography>
                      <Typography variant="body1">{contact.email}</Typography>
                    </Box>
                  </Box>
                )}

                {contact.address && (
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <LocationIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        العنوان
                      </Typography>
                      <Typography variant="body1">
                        {[
                          contact.address.street,
                          contact.address.city,
                          contact.address.state,
                          contact.address.country,
                          contact.address.postalCode
                        ].filter(Boolean).join(', ') || 'غير محدد'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  معلومات الحساب
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {contact.linkedAccount && contact.accountId ? (
                  <>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <AccountBalanceIcon color="success" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          رقم الحساب
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {contact.accountId.accountNumber}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box sx={{ width: 40 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          اسم الحساب
                        </Typography>
                        <Typography variant="body1">
                          {contact.accountId.name}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">
                    لا يوجد حساب مرتبط بهذا {contact.type === 'customer' ? 'العميل' : 'المورد'}
                  </Alert>
                )}

                {contact.taxNumber && (
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Box sx={{ width: 40 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        الرقم الضريبي
                      </Typography>
                      <Typography variant="body1">{contact.taxNumber}</Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper elevation={2}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: '1rem'
              }
            }}
          >
            <Tab label="سجل الحركات" />
            <Tab label="نقاط الولاء" icon={<StarsIcon />} iconPosition="start" />
            <Tab label="معلومات إضافية" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <ContactTransactions contactId={contact._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <CustomerLoyaltyPoints contactId={contact._id} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box p={2}>
              <Grid container spacing={3}>
                {/* الفروع */}
                {contact.branches && contact.branches.length > 0 && (
                  <Grid item xs={12}>
                    <Card elevation={1}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                          الفروع
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {contact.branches.map((branchId, index) => {
                            const branch = branches.find(b => b._id === branchId);
                            return (
                              <Chip
                                key={index}
                                label={branch ? branch.name : branchId}
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* الملاحظات */}
                {contact.notes && contact.notes.length > 0 && (
                  <Grid item xs={12}>
                    <Card elevation={1}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                          الملاحظات
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" flexDirection="column" gap={2}>
                          {contact.notes.map((note, index) => (
                            <Paper key={index} elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                              <Typography variant="body1" sx={{ mb: 1 }}>
                                {note.content}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(note.createdAt).toLocaleDateString('ar-SA', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    تاريخ الإنشاء
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(contact.createdAt).toLocaleDateString('ar-SA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    آخر تحديث
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(contact.updatedAt).toLocaleDateString('ar-SA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Paper>
      </Box>
    </Box>
  );
};

export default ContactDetails;